import * as React from 'react'
import {
  IonContent,
  IonPage,
  IonHeader,
  IonSegment,
  IonSegmentButton,
  IonLoading,
  IonButton,
} from '@ionic/react'
import { useQuery } from '@tanstack/react-query'

import { client } from '../services/client'
import { useAccount } from '../contexts/authContext'
import CreatePostFAB from '../components/fab/create-post-fab'
import Header from '../components/header'
import Feed from '../components/home/feed/feed'
import Lists from '../components/home/lists/lists'
import { Card, CardContent } from '../components/ui/card'
import { isSocialOnly } from '../auth/utils'
import { useLoyaltyPrograms } from '../loyalty/queries'

type CurrentSegment = 'home' | 'lists' | 'feed'

function Home() {
  const account = useAccount()
  const loyaltyEnabled = account.isAdmin && account.account.settings.loyalty
  const [currentSegment, setCurrentSegment] = React.useState<CurrentSegment>(
    loyaltyEnabled && !account.isEnterprise ? 'home' : 'lists'
  )

  // HACK: ACCOUNT EMPTY OBJECT WORKAROUND
  if (!Object.keys(account).length) return null

  return (
    <IonPage id="home">
      <CreatePostFAB />
      <IonHeader>
        <Header showMenu title="Home" />
      </IonHeader>
      <IonContent className="ion-padding-vertical space-y-6">
        <>
          {isSocialOnly(account) ? (
            <Feed />
          ) : (
            <>
              <div className="ion-padding-horizontal mx-auto max-w-xl">
                <IonSegment
                  value={currentSegment}
                  onIonChange={(e) =>
                    setCurrentSegment(e.detail.value as CurrentSegment)
                  }
                >
                  {loyaltyEnabled && !account.isEnterprise ? (
                    <IonSegmentButton value="home">Home</IonSegmentButton>
                  ) : null}
                  <IonSegmentButton value="lists">Lists</IonSegmentButton>
                  <IonSegmentButton value="feed">Feed</IonSegmentButton>
                </IonSegment>
              </div>

              {currentSegment === 'lists' ? <Lists /> : null}

              {currentSegment === 'feed' ? <Feed /> : null}

              {currentSegment === 'home' ? <Loyalty /> : null}
            </>
          )}
        </>
      </IonContent>
    </IonPage>
  )
}

function Loyalty() {
  const loyaltyPrograms = useLoyaltyPrograms()
  const loyaltyStats = useQuery({
    queryKey: [{ entity: 'loyalty', scope: 'stats' }],
    queryFn: async () => {
      const response: {
        result: {
          loyaltyMembersCount: number
          loyaltyMembersWithPointsCount: number
        }
      } = await client.post('functions/loyaltyMemberStats').json()

      return response.result
    },
  })

  if (loyaltyPrograms.data && loyaltyStats.data) {
    return (
      <>
        {!loyaltyPrograms.data.data.length ? (
          <div className="mx-auto flex flex-col items-center gap-y-6">
            <p className="text-2xl font-normal">
              You haven't created any Loyalty Programs.
            </p>
            <IonButton
              color="yellow"
              routerLink="/admin/loyalty-programs"
              routerDirection="none"
            >
              Create Loyalty Program
            </IonButton>
          </div>
        ) : null}
        <div className="ion-padding mx-auto flex max-w-lg gap-x-4">
          <Card>
            <CardContent>
              <div className="text-center">
                <p className="text-xl">Loyalty Members</p>
                <p>{loyaltyStats.data.loyaltyMembersCount}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <div className="text-center">
                <p className="text-xl">Members With Rewards</p>

                <p>{loyaltyStats.data.loyaltyMembersWithPointsCount}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    )
  }

  return <IonLoading isOpen message="Loading Loyalty Programs..." />
}

export default Home
